import React, { useEffect, useRef, useState } from 'react'
import { Button, Drawer, Space } from 'antd'
import { EditOutlined, EyeOutlined } from '@ant-design/icons'
import { PQB_EDITOR_OVERLAY_CLASS, type ValueInputProps } from '@pushly/aqe'
import { attachPolygonMap, type PolygonMapHandle, type PolygonPoint } from './geo-polygon-map'

import './geo-polygon-input.scss'

/** The engine's stored shape for a custom-location condition: a polygon's vertices. */
interface GeoPolygonValue {
    points: PolygonPoint[]
}

/** Shown in place of the map where the page's mapping library never loaded. */
const MAP_UNAVAILABLE = 'The map could not be loaded. Reload the page to draw an area.'

/** The polygon a value holds, or an empty list when the value is absent or malformed. */
function readPoints(value: GeoPolygonValue | null | undefined): PolygonPoint[] {
    return value && Array.isArray(value.points) ? value.points : []
}

function samePolygon(a: PolygonPoint[], b: PolygonPoint[]): boolean {
    return JSON.stringify(a) === JSON.stringify(b)
}

/**
 * The corners of a polygon, which is one fewer than the positions it stores.
 *
 * A GeoJSON linear ring repeats its first position as its last, so the map hands back four positions
 * for a triangle and every stored polygon carries the repeat. Counting positions therefore reports
 * one more corner than the reader placed — on a drawing they just made, and on every polygon already
 * stored, since they were all authored through the same closed-ring flow.
 *
 * The repeat is only dropped when it is actually a repeat: an unclosed ring is counted as it stands
 * rather than having a real corner taken off it.
 */
function vertexCount(points: PolygonPoint[]): number {
    const closed = points.length > 1 && samePolygon([points[0]], [points[points.length - 1]])
    return closed ? points.length - 1 : points.length
}

/** What the row says about the polygon it holds, in the reader's terms. */
function summarize(points: PolygonPoint[]): string {
    const corners = vertexCount(points)
    if (corners === 0) return 'No area drawn'
    return `Area drawn (${corners} ${corners === 1 ? 'point' : 'points'})`
}

/**
 * The value control for a custom-location condition — a polygon a subscriber's location
 * falls inside. The operator is fixed to `geo_includes` by the registry, so there is no
 * operator choice; all this authors is the polygon, stored as `{ points: [[lng, lat], …] }`.
 *
 * The row itself shows only a summary and a button; the drawing happens in a drawer holding
 * a Mapbox map. Every line that touches Mapbox lives in {@link attachPolygonMap}, reached
 * through the page globals it needs, so this component holds only the working polygon and
 * runs with no map at all — which is how it is exercised where those globals are absent. The
 * map is seeded from the committed value and reports the drawn polygon back through its
 * `onChange`; nothing here calls a server.
 *
 * A map sizes its canvas from the container it is given, so the drawer's height has to reach the map
 * element before the map reads it — see the stylesheet. The timing is what guarantees that: the map
 * is built from the drawer's `afterOpenChange`, by which point the container has finished animating
 * and is already at its final size, and the map re-reads it once more when its tiles load. Nothing
 * has to be told to resize afterwards. Where the globals are missing, or construction throws, there
 * is no map at all and the drawer says so rather than showing an empty box.
 *
 * A polygon is committed only on Save, and Save stays disabled until one has been drawn.
 * That is deliberate: the registry forces `geo_includes` and a fresh row starts on an empty
 * `{ points: [] }`, and committing that would be a malformed condition the reader never drew.
 * The drawer holds the in-progress polygon so closing it without saving leaves the committed
 * value untouched.
 */
export function GeoPolygonInput({
    value,
    onChange,
    disabled,
    size,
    // The editor supplies this; the fallback is for rendering the control outside one.
    getOverlayContainer = () => document.body,
}: ValueInputProps<GeoPolygonValue | null>) {
    const committed = readPoints(value)

    const [open, setOpen] = useState(false)
    const [working, setWorking] = useState<PolygonPoint[]>(committed)
    const [mapAttached, setMapAttached] = useState(true)
    const containerRef = useRef<HTMLDivElement>(null)
    const handleRef = useRef<PolygonMapHandle | null>(null)

    // Stands the map up in the drawer, or reports that it could not be.
    //
    // Called once the drawer has finished opening rather than when `open` flips: the drawer
    // mounts its body into a portal after that state change, so the container does not exist
    // yet on the opening render, and a map attached then would measure a box still sliding
    // into place. By the time the open has settled the container is mounted at its final size,
    // which is the size the map has to read.
    const attachMap = () => {
        if (!containerRef.current || handleRef.current) return

        // Absent globals are reported by `attachPolygonMap` returning null. A library that is present
        // but throws while constructing is the other way this fails, and it used to escape here —
        // leaving `mapAttached` true, so the reader got an empty box rather than the notice that says
        // the map is unavailable. Both paths now end in the same place.
        try {
            handleRef.current = attachPolygonMap({
                container: containerRef.current,
                points: committed,
                interactive: !disabled,
                onChange: (points) => setWorking(points),
            })
        } catch {
            handleRef.current = null
        }

        setMapAttached(handleRef.current !== null)
    }

    const detachMap = () => {
        handleRef.current?.destroy()
        handleRef.current = null
    }

    // A closed drawer holds no live map, so each open starts from the committed polygon. The
    // teardown also covers unmounting while the drawer is open.
    useEffect(() => {
        if (!open) detachMap()
        return detachMap
    }, [open])

    // An open drawer is the topmost thing on the page and so owns Escape, but antd does not close
    // a Drawer on it. Left unhandled the key reaches the condition editor underneath, which reads
    // it as cancelling the edit and drops a row that was never saved — so the drawer takes it.
    useEffect(() => {
        if (!open) return

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false)
        }

        document.addEventListener('keydown', onKeyDown)
        return () => document.removeEventListener('keydown', onKeyDown)
    }, [open])

    const openDrawer = () => {
        setWorking(committed)
        setMapAttached(true)
        setOpen(true)
    }

    const closeDrawer = () => setOpen(false)

    const save = () => {
        onChange({ points: working })
        setOpen(false)
    }

    const canSave = !disabled && working.length > 0 && !samePolygon(working, committed)

    const editLabel = disabled ? 'View area' : committed.length > 0 ? 'Edit area' : 'Draw area'

    return (
        <div className="segment-geo-polygon">
            <Space size="small" align="center">
                <span className="segment-geo-polygon__summary">{summarize(committed)}</span>
                <Button size={size ?? 'small'} onClick={openDrawer}>
                    {disabled ? <EyeOutlined /> : <EditOutlined />}
                    <span>{editLabel}</span>
                </Button>
            </Space>

            <Drawer
                className="segment-geo-polygon__drawer"
                title={disabled ? 'View area' : 'Draw area'}
                placement="right"
                size={900}
                open={open}
                onClose={closeDrawer}
                // A drawer needs the viewport to size itself against, so it renders at the
                // document root rather than in the editor panel the row anchors its menus to —
                // that panel is a small absolutely positioned box, and a drawer confined to it
                // gives the map no height to measure.
                getContainer={getOverlayContainer}
                // Rendering at the root puts every click in the drawer outside the editor's own
                // subtree, which the editor would otherwise read as a click away and dismiss on —
                // discarding the condition before Save can commit it. This class declares the
                // drawer as the editor's, so the editor stays open while the area is drawn. It is
                // load-bearing with `getContainer` above: neither works without the other.
                rootClassName={PQB_EDITOR_OVERLAY_CLASS}
                // The drawer mounts its body after `open` changes and then animates into place,
                // so this is the first moment the map has a container at the size the reader
                // will actually see. Attaching here rather than on the open edge is what gives
                // the map something to measure; tearing down on close keeps each open fresh.
                afterOpenChange={(opened) => {
                    if (opened) attachMap()
                    else detachMap()
                }}
                footer={
                    disabled ? null : (
                        <Space>
                            <Button onClick={closeDrawer}>Cancel</Button>
                            <Button type="primary" disabled={!canSave} onClick={save}>
                                Save area
                            </Button>
                        </Space>
                    )
                }
            >
                <div className="segment-geo-polygon__map-well">
                    {!mapAttached && <p className="segment-geo-polygon__unavailable">{MAP_UNAVAILABLE}</p>}
                    {/*
                     * The map is the whole authoring affordance for this condition, so it needs a name
                     * and a role: without them a screen reader reaches an unlabelled box and is told
                     * nothing about what it is or what it already holds. `application` is the honest
                     * role while it can be drawn on, `img` once it is only being looked at.
                     *
                     * This is a partial answer. Polygons are still drawn by pointer and there is no
                     * keyboard path to placing or moving a vertex, which is a WCAG 2.1.1 failure on a
                     * route `a11y.config.ts` gates at AA. Naming the surface does not fix that; it
                     * makes the surface findable and its contents readable, which is the part that
                     * costs nothing. A text path to the same value — the stored shape is only
                     * `[[lng, lat], …]` — is the real fix and is tracked separately.
                     */}
                    <div
                        ref={containerRef}
                        className="segment-geo-polygon__map"
                        role={disabled ? 'img' : 'application'}
                        aria-label={`Map for drawing a target area. ${summarize(working)}.`}
                    />
                </div>
            </Drawer>
        </div>
    )
}
